import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  Briefcase,
  X,
  Save,
  User,
  AlertCircle,
  Grid,
  List,
  Award,
  Coins,
  CheckCircle,
  XCircle,
  Calendar,
  Eye,
} from 'lucide-react';
import { formatCurrency, formatPhoneNumber } from '../utils/currency';

const TeamMembers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberSummaries, setMemberSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    employee_id: '',
    id_number: '',
    phone: '',
    email: '',
    address: '',
    emergency_contact: '',
    emergency_contact_phone: '',
    skills: '',
    hourly_rate: 0,
    job_description: '',
    bibc_job_code: '',
    role: 'worker',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await teamMembersAPI.getAll();
      setMembers(response.data);
      
      // Load summaries for each member (for has_medical and leave_days)
      const summaries = {};
      for (const member of response.data) {
        try {
          const summaryRes = await teamMembersAPI.getSummary(member.id);
          summaries[member.id] = summaryRes.data;
        } catch (e) {
          summaries[member.id] = { has_valid_medical: false, annual_leave_days: 0 };
        }
      }
      setMemberSummaries(summaries);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name || '',
        employee_id: member.employee_id || '',
        id_number: member.id_number || '',
        phone: member.phone || '',
        email: member.email || '',
        address: member.address || '',
        emergency_contact: member.emergency_contact || '',
        emergency_contact_phone: member.emergency_contact_phone || '',
        skills: member.skills?.join(', ') || '',
        hourly_rate: member.hourly_rate || 0,
        job_description: member.job_description || '',
        bibc_job_code: member.bibc_job_code || '',
        role: member.role || 'worker',
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        employee_id: '',
        id_number: '',
        phone: '',
        email: '',
        address: '',
        emergency_contact: '',
        emergency_contact_phone: '',
        skills: '',
        hourly_rate: 0,
        job_description: '',
        bibc_job_code: '',
        role: 'worker',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const memberData = {
      ...formData,
      skills: formData.skills.split(',').map(s => s.trim()).filter(Boolean),
      hourly_rate: parseFloat(formData.hourly_rate) || 0,
    };

    try {
      if (editingMember) {
        await teamMembersAPI.update(editingMember.id, memberData);
        toast.success('Team member updated successfully');
      } else {
        const result = await teamMembersAPI.create(memberData);
        toast.success('Team member created successfully');
        // Navigate to new member's detail page to add certifications, PPE, etc.
        if (result.data?.id) {
          navigate(`/team-members/${result.data.id}`);
        }
      }
      setShowModal(false);
      loadMembers();
    } catch (error) {
      toast.error(`Failed to ${editingMember ? 'update' : 'create'} team member`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      await teamMembersAPI.delete(id);
      toast.success('Team member deleted successfully');
      loadMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.job_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team Members
          </h1>
          <p className="text-slate-600 mt-1">
            Manage non-system employees and contractors
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-5 h-5" />
            Add Team Member
          </button>
        )}
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, employee ID, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Members Count */}
      <p className="text-sm text-slate-600 mb-4">{filteredMembers.length} team member(s) found</p>

      {/* Members Grid/List */}
      {filteredMembers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No team members found</h3>
          <p className="text-slate-600 mb-4">Get started by adding your first team member.</p>
          {canEdit && (
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Team Member
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((member) => {
            const summary = memberSummaries[member.id] || {};
            return (
            <div 
              key={member.id} 
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/team-members/${member.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{member.name}</h3>
                    {member.employee_id && (
                      <span className="text-sm text-slate-500">ID: {member.employee_id}</span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenModal(member); }}
                      className="p-1 hover:bg-slate-100 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(member.id); }}
                      className="p-1 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-2 mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  summary.has_valid_medical ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {summary.has_valid_medical ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Medical
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  <Calendar className="w-3 h-3" />
                  {summary.annual_leave_days || 0} days
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                {member.job_description && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Briefcase className="w-4 h-4" />
                    <span>{member.job_description}</span>
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{formatPhoneNumber(member.phone)}</span>
                  </div>
                )}
                {member.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span>{member.email}</span>
                  </div>
                )}
                {member.hourly_rate > 0 && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Coins className="w-4 h-4" />
                    <span>{formatCurrency(member.hourly_rate)}/hr</span>
                  </div>
                )}
              </div>

              {member.skills?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1">
                  {member.skills.slice(0, 3).map((skill, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                      {skill}
                    </span>
                  ))}
                  {member.skills.length > 3 && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                      +{member.skills.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Employee ID</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Role</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Contact</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-slate-700">Has Medical</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-slate-700">Leave Days</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Hourly Rate</th>
                {canEdit && <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredMembers.map((member) => {
                const summary = memberSummaries[member.id] || {};
                return (
                  <tr key={member.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/team-members/${member.id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-900">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{member.employee_id || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{member.job_description || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{member.phone ? formatPhoneNumber(member.phone) : member.email || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/team-members/${member.id}?tab=certifications`); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          summary.has_valid_medical 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {summary.has_valid_medical ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {summary.has_valid_medical ? 'Yes' : 'No'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/team-members/${member.id}?tab=leave`); }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        <Calendar className="w-3 h-3" />
                        {summary.annual_leave_days || 0} days
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{member.hourly_rate ? formatCurrency(member.hourly_rate) : '-'}</td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/team-members/${member.id}`); }}
                            className="p-1 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-blue-500" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenModal(member); }}
                            className="p-1 hover:bg-slate-100 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-slate-500" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(member.id); }}
                            className="p-1 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Employee ID</label>
                    <input
                      type="text"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ID Number</label>
                    <input
                      type="text"
                      value={formData.id_number}
                      onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="worker">Worker</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="foreman">Foreman</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData({ ...formData, phone: formatted });
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+27 21 123 4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData({ ...formData, emergency_contact_phone: formatted });
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+27 21 123 4567"
                    />
                  </div>
                </div>
              </div>

              {/* Work Details */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Work Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Job Description/Role</label>
                    <input
                      type="text"
                      value={formData.job_description}
                      onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                      placeholder="e.g., Electrician, Plumber"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">BIBC Job Code</label>
                    <input
                      type="text"
                      value={formData.bibc_job_code}
                      onChange={(e) => setFormData({ ...formData, bibc_job_code: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate (R)</label>
                    <input
                      type="text"
                      value={formData.hourly_rate}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setFormData({ ...formData, hourly_rate: value });
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Skills
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Skills (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.skills}
                      onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                      placeholder="e.g., Electrical, HVAC, Welding"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Link to Extended Features (for existing members) */}
              {editingMember && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>Note:</strong> Certifications, PPE, and Leave are managed in the member&apos;s detail page.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); navigate(`/team-members/${editingMember.id}`); }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Go to Member Details →
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingMember ? 'Update' : 'Create'} Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
